import { HttpErrorResponse } from '@angular/common/http';

import { Selector } from '@ngrx/store';

import { ConfigObject, MakeRef, MetaState, PaginationState, Reference } from '../types';

export type ID = string | number;
export const DEFAULT_SCOPE_NAME = 'main';
export type Scope = typeof DEFAULT_SCOPE_NAME | string;
export type IdSelector<T> = (e: T) => ID;
export type OptimisticUpdate<T extends BaseEntity> = { update: EntityPartial<T>; before: T };
export interface EntityState<T extends BaseEntity, U extends MetaOptions = MetaOptions> {
  entityMap: Record<ID, T | null>;
  entityMetaMap: Record<ID, MetaState>;
  scope: Record<Scope, ID[]>;
  scopeMeta: Record<Scope, MetaState<InferScopeMeta<U>>>;
  aliases: Record<ID, ID>;
  optimisticUpdate: Record<string, OptimisticUpdate<T>>;
  optimisticDelete: Record<ID, T>;
  scopePagination: Record<Scope, PaginationState>;
}

export interface CorrelationId {
  correlationId?: string;
}

export interface EntityAdapterOptions {
  scope?: string;
}

export interface PrependingOption {
  prepend?: boolean;
}

export interface BaseEntity {
  [key: string]: any;
}

export interface MetaOptions {
  scopeMeta?: object;
}

export type EntityPartial<T extends BaseEntity> = Partial<T>;

export type BaseSelector<T extends BaseEntity, U extends MetaOptions> = Selector<
  any,
  EntityState<T, U>
>;

export type MetaScopeOptions = { scope: Scope | null };
export type MetaEntitiesOptions = { entities: ID[] };
export type MetaActionOptions = MetaScopeOptions & MetaEntitiesOptions;

export type InferScopeMeta<T> = T extends { scopeMeta?: infer R } ? R : never;

export interface EntityMetaAdapter<T extends BaseEntity, S extends MetaOptions> {
  markAsPending: <T1 extends BaseEntity>(
    state: EntityState<T1, S>,
    options?: MetaActionOptions,
  ) => EntityState<T1, S>;
  markAsLoaded: <T1 extends BaseEntity>(
    state: EntityState<T1, S>,
    options?: MetaActionOptions,
  ) => EntityState<T1, S>;
  setError: (
    state: EntityState<T, S>,
    error: HttpErrorResponse,
    options?: MetaActionOptions,
  ) => EntityState<T, S>;
  setPaginationMeta: (
    state: EntityState<T, S>,
    paginationMeta: PaginationState,
    options?: MetaScopeOptions,
  ) => EntityState<T, S>;
  // alias correlationId -> id for remote create operations
  setAlias: (state: EntityState<T, S>, data: Record<ID, ID>) => EntityState<T, S>;
  setScopeMetaCustom: (
    state: EntityState<T, S>,
    data: InferScopeMeta<S>,
    options?: MetaScopeOptions,
  ) => EntityState<T, S>;
}

export interface EntityDataAdapter<
  T extends BaseEntity,
  U extends MetaOptions,
  S extends EntityState<T, U> = EntityState<T, U>,
> {
  // Add to scope (uniq), set entity
  addOne: (state: S, data: T, options?: EntityAdapterOptions & CorrelationId) => S;
  addMany: (state: S, data: T[], options?: EntityAdapterOptions & PrependingOption) => S;

  // Set scope, set entity
  setOne: (state: S, data: T, options: EntityAdapterOptions) => S;
  setMany: (state: S, data: T[], options: EntityAdapterOptions) => S;

  // Doesn't touch scope, set entity
  writeOne: (state: S, data: T) => S;
  writeMany: (state: S, data: T[]) => S;

  // Replace entity in all scopes, set entity
  replaceOne: (state: S, previousId: ID, current: T) => S;
  replaceMany: (state: S, data: { previousId: ID; current: T }[]) => S;

  // Removes entity from all scopes, removes entity
  removeOne: (state: S, id: ID) => S;
  removeMany: (state: S, ids: ID[]) => S;

  // Doesn't touch scope, updates entity (spread)
  updateOne: (state: S, data: EntityPartial<T>) => S;
  updateMany: (state: S, data: EntityPartial<T>[]) => S;

  // Doesn't touch scope, updates or creates entity (spread)
  updateOrCreateMany: (state: S, data: T[]) => S;

  // Add to scope if not exist, update or add entity
  upsertOne: (state: S, data: T, options?: EntityAdapterOptions) => S;
  upsertMany: (state: S, data: T[], options?: EntityAdapterOptions) => S;

  optimisticUpdateSet: (state: S, correlationId: ID, data: OptimisticUpdate<T>) => S;
  optimisticUpdateRemove: (state: S, correlationId: string) => S;
  optimisticDeleteSet: (state: S, data: T) => S;
  optimisticDeleteRemove: (state: S, id: ReturnType<IdSelector<T>>) => S;

  // Remove all entities from scope
  resetScope: (state: S, scope?: Scope) => S;
  // Reset to initial state
  resetAll: (state: S) => S;
}

export type EntityCollectionAdapter<
  T extends BaseEntity,
  U extends MetaOptions,
> = EntityMetaAdapter<T, U> & EntityDataAdapter<T, U>;

export type EntityCollectionCustomActionsCreator<T, U extends MetaOptions> = (
  adapter: EntityCollectionAdapter<T, U>,
) => ConfigObject<EntityState<T, U>>;

export interface EntityCollectionStoreOptions<T, U extends MetaOptions> {
  idSelector?: IdSelector<T>;
  customActions?: EntityCollectionCustomActionsCreator<T, U>;
  references?: (makeRef: MakeRef<T>) => Reference<T>[];
  initialState?: EntityState<T>;
}

export type EntityCollectionStoreCustomActions<T extends EntityCollectionStoreOptions<any, any>> =
  T['customActions'] extends EntityCollectionCustomActionsCreator<any, any>
    ? ReturnType<T['customActions']>
    : {};
